/**
 * Standardized chart configuration constants
 * These values ensure consistent chart rendering across web and native platforms
 */

export const CHART_CONFIG = {
  // Standard padding values for all charts
  DOMAIN_PADDING: {
    left: 20,
    right: 40,
    top: 18,
  },

  // Standard padding for chart containers
  CONTAINER_PADDING: {
    bottom: 0,
  },

  // Standard spacing for line/scatter charts
  STANDARD_SPACE: 8,

  // Standard chart dimensions
  DIMENSIONS: {
    // Standard chart height for most charts
    STANDARD_HEIGHT: "h-64",
    // Taller height for comparison charts
    COMPARISON_HEIGHT: "h-96",
  },

  // Standard bar configuration
  BAR: {
    WIDTH: 25,
    ROUNDED_CORNERS: {
      topLeft: 4,
      topRight: 4,
    },
  },

  // Standard line/scatter configuration
  LINE: {
    STROKE_WIDTH: 2,
    SCATTER_RADIUS: 4,
  },
} as const;

/**
 * Font configuration for chart axes.
 */
export interface ChartFont {
  family?: string;
  size?: number;
  weight?: string | number;
}

/**
 * Axis configuration returned by getStandardAxisConfig.
 */
export interface StandardAxisConfig {
  xAxis: {
    font: ChartFont;
    labelColor: string;
    lineColor: string;
    lineWidth: number;
    formatXLabel: (value: unknown) => string;
  };
  yAxis: {
    font: ChartFont;
    labelColor: string;
    lineColor: string;
    lineWidth: number;
  }[];
}

/**
 * Get standard axis configuration for charts.
 * @remarks
 * Provides consistent axis styling across all charts in the application.
 * Y-axis labels can be hidden by setting showYAxisLabels to false, which
 * applies transparent color to the labels.
 * @param font - Font configuration object for axis labels
 * @param labelColor - Color string for axis label text
 * @param showYAxisLabels - Whether to display Y-axis labels or hide them with transparent color
 * @returns Standard axis configuration object for chart libraries
 */
export const getStandardAxisConfig = (
  font: ChartFont,
  labelColor: string,
  showYAxisLabels: boolean = false
): StandardAxisConfig => ({
  xAxis: {
    font,
    labelColor,
    lineColor: "transparent",
    lineWidth: 0,
    formatXLabel: (value: unknown) => value?.toString() || "",
  },
  yAxis: [
    {
      font,
      labelColor: showYAxisLabels ? labelColor : "transparent",
      lineColor: "transparent",
      lineWidth: 0,
    },
  ],
});
